import {
  normalizeLegacyCanvasBackgroundColor,
  normalizeLegacyGaugeNeedleColor,
  normalizeLegacySemanticStyleColor,
} from "./theme-aware-legacy-colors";

export const PROJECT_THEME_COLOR_MIGRATION_VERSION = 10;

const STYLE_SLOT_KEYS = ["base", "mobile", "tablet", "desktop", "dark", "light"] as const;
const COMPONENT_PROPERTY_COLOR_KEYS: Record<string, string> = {
  background: "background",
  backgroundColor: "backgroundColor",
  border: "border",
  borderColor: "borderColor",
  cardBackgroundColor: "backgroundColor",
  cardBorderColor: "borderColor",
  activeColor: "color",
  color: "color",
  hoverColor: "color",
  iconColor: "color",
  infoIconColor: "color",
  labelColor: "color",
  navigationIconColor: "color",
  scaleColor: "color",
  textColor: "color",
  valueColor: "color",
  subtitleColor: "color",
  unitColor: "color",
};

type MutableRecord = Record<string, unknown>;

type ComponentColorContext = {
  id?: unknown;
  name?: unknown;
  type?: unknown;
  parentId?: unknown;
};

export function migrateProjectThemeColors<T>(projectLike: T): T {
  const migrated = cloneValue(projectLike);

  if (Array.isArray(migrated)) {
    migrateComponentArray(migrated);
    return migrated;
  }

  if (!isRecord(migrated)) {
    return migrated;
  }

  const migratedRecord: MutableRecord = migrated;

  if (getThemeColorMigrationVersion(migratedRecord) >= PROJECT_THEME_COLOR_MIGRATION_VERSION) {
    return migrated;
  }

  migrateProjectLikeRecord(migratedRecord);

  if (shouldVersionRecord(migratedRecord)) {
    const metadata = isRecord(migratedRecord.metadata) ? migratedRecord.metadata : {};
    migratedRecord.metadata = {
      ...metadata,
      themeColorMigrationVersion: PROJECT_THEME_COLOR_MIGRATION_VERSION,
    };
  }

  return migrated;
}

function migrateProjectLikeRecord(record: MutableRecord): void {
  if (isComponentRecord(record)) {
    migrateComponent(record);
  }

  migrateCanvasSettings(record.canvasSettings);

  if (Array.isArray(record.components)) {
    migrateComponentArray(record.components);
  }

  migratePages(record.pages);
  migrateVersions(record.versions);

  if (isRecord(record.snapshot)) {
    migrateSnapshot(record.snapshot);
  }

  if (isRecord(record.page)) {
    migratePage(record.page);
  }
}

function migrateVersions(versions: unknown): void {
  const versionRecords = Array.isArray(versions)
    ? versions
    : isRecord(versions)
      ? Object.values(versions)
      : [];

  for (const version of versionRecords) {
    if (!isRecord(version) || !isRecord(version.snapshot)) continue;
    migrateSnapshot(version.snapshot);
  }
}

function migrateSnapshot(snapshot: MutableRecord): void {
  migrateCanvasSettings(snapshot.canvasSettings);

  if (Array.isArray(snapshot.components)) {
    migrateComponentArray(snapshot.components);
  }

  migratePages(snapshot.pages);
}

function migratePages(pages: unknown): void {
  if (!Array.isArray(pages)) return;

  for (const pageLike of pages) {
    if (!isRecord(pageLike)) continue;

    if (isRecord(pageLike.page)) {
      migratePage(pageLike.page);
    } else {
      migratePage(pageLike);
    }

    if (Array.isArray(pageLike.components)) {
      migrateComponentArray(pageLike.components);
    }
  }
}

function migratePage(page: MutableRecord): void {
  migrateCanvasSettings(page.canvasSettings);

  if (Array.isArray(page.components)) {
    migrateComponentArray(page.components);
  }
}

function migrateComponentArray(components: unknown[]): void {
  for (const component of components) {
    if (isRecord(component)) {
      migrateComponent(component);
    }
  }
}

function migrateComponent(component: MutableRecord): void {
  if (isRecord(component.style)) {
    migrateComponentStyle(component.style, {
      id: component.id,
      name: component.name,
      type: component.type,
      parentId: component.parentId,
    });
  }

  if (isRecord(component.properties)) {
    migrateComponentStyleLikeProperties(component.properties, {
      id: component.id,
      name: component.name,
      type: component.type,
      parentId: component.parentId,
    });
  }

  if (Array.isArray(component._childComponents)) {
    migrateComponentArray(component._childComponents);
  }
}

function migrateComponentStyle(
  style: MutableRecord,
  context: ComponentColorContext,
): void {
  const hasStyleSlots = STYLE_SLOT_KEYS.some((slot) => isRecord(style[slot]));

  if (hasStyleSlots) {
    for (const slot of STYLE_SLOT_KEYS) {
      const slotStyle = style[slot];
      if (isRecord(slotStyle)) {
        migrateStyleProperties(slotStyle, context);
      }
    }
  }

  migrateStyleProperties(style, context);
}

function migrateStyleProperties(
  style: MutableRecord,
  context: ComponentColorContext,
): void {
  for (const [property, value] of Object.entries(style)) {
    if (STYLE_SLOT_KEYS.includes(property as typeof STYLE_SLOT_KEYS[number])) {
      continue;
    }
    if (typeof value !== "string") continue;

    const migratedValue = normalizeLegacySemanticStyleColor(property, value, context);
    if (migratedValue !== value) {
      style[property] = migratedValue;
    }
  }
}

function migrateComponentStyleLikeProperties(
  properties: MutableRecord,
  context: ComponentColorContext,
): void {
  const needleColor = properties.needleColor;
  if (typeof needleColor === "string") {
    const migratedNeedleColor = normalizeLegacyGaugeNeedleColor(needleColor);
    if (migratedNeedleColor !== needleColor) {
      properties.needleColor = migratedNeedleColor;
    }
  }

  for (const [property, colorProperty] of Object.entries(COMPONENT_PROPERTY_COLOR_KEYS)) {
    const value = properties[property];
    if (typeof value !== "string") continue;

    const migratedValue = normalizeLegacySemanticStyleColor(colorProperty, value, context);
    if (migratedValue !== value) {
      properties[property] = migratedValue;
    }
  }
}

function migrateCanvasSettings(canvasSettings: unknown): void {
  if (!isRecord(canvasSettings)) return;

  const background = canvasSettings.background;
  if (isRecord(background) && typeof background.color === "string") {
    background.color = normalizeLegacyCanvasBackgroundColor(background.color);
  }

  const grid = canvasSettings.grid;
  if (!isRecord(grid)) return;

  if (typeof grid.color === "string") {
    grid.color = normalizeLegacySemanticStyleColor("borderColor", grid.color);
  }

  const majorLines = grid.majorLines;
  if (isRecord(majorLines) && typeof majorLines.color === "string") {
    majorLines.color = normalizeLegacySemanticStyleColor("borderColor", majorLines.color);
  }
}

function getThemeColorMigrationVersion(record: MutableRecord): number {
  const metadata = isRecord(record.metadata) ? record.metadata : undefined;
  const version = metadata?.themeColorMigrationVersion;
  return typeof version === "number" && Number.isFinite(version) ? version : 0;
}

function shouldVersionRecord(record: MutableRecord): boolean {
  if (typeof record.id !== "string" || isComponentRecord(record)) {
    return false;
  }

  if (
    record.currentVersion !== undefined ||
    record.isMultiPage !== undefined ||
    record.settings !== undefined ||
    Array.isArray(record.versions) ||
    isRecord(record.versions)
  ) {
    return true;
  }

  return (
    typeof record.name === "string" &&
    record.projectId === undefined &&
    (Array.isArray(record.pages) ||
      Array.isArray(record.components) ||
      isRecord(record.canvasSettings))
  );
}

function isComponentRecord(record: MutableRecord): boolean {
  return typeof record.type === "string" && isRecord(record.style);
}

function cloneValue<T>(value: T): T {
  return structuredClone(value);
}

function isRecord(value: unknown): value is MutableRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
